import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import SignaturePad from "react-signature-canvas";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TitleBar from "../_common/TitleBar";
import contractorActions from "../../_actions/contractorActions";
import userActions from "../../_actions/userActions";
import cem4601PdfActions from "../../_actions/cem4601PdfActions";
import { USER } from "../../_constants/actionConstants";
import "./styles/contractorWeeklyPrint.css";

const DAYS = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

function daysWorked(dates) {
  const worked = new Set((dates || []).map((d) => moment(d).day()));
  return DAYS.map((_, i) => worked.has(i));
}

function DayCheckboxes({ checked, none }) {
  return (
    <div className="cwp-days-grid">
      {DAYS.map((d, i) => (
        <label key={d.key} className="cwp-day-box">
          <input type="checkbox" checked={checked[i]} readOnly /> {d.label}
        </label>
      ))}
      <label className="cwp-day-box">
        <input type="checkbox" checked={!!none} readOnly /> None
      </label>
    </div>
  );
}

class ContractorWeeklyReport extends Component {
  state = {
    projectIdNumber: this.props.jobStamp?.efis || "",
    reName: this.props.user?.fullName || "",
    signDate: moment().format("YYYY-MM-DD"),
    signature: null,
  };

  signPadRef = React.createRef();

  componentDidMount() {
    const { tab, dispatch, projContractorList, user } = this.props;
    if (!projContractorList) dispatch(contractorActions.getAllForProject(tab.projectId));
    if (user?.userId) dispatch(userActions.getSignature(user.userId, true));
  }

  componentDidUpdate(prevProps) {
    const { user, dispatch, existSignatureData } = this.props;

    if (!prevProps.user?.userId && user?.userId)
      dispatch(userActions.getSignature(user.userId, true));

    if (prevProps.existSignatureData !== existSignatureData && existSignatureData?.dataUrl)
      this.setState({ signature: existSignatureData.dataUrl });
  }

  componentWillUnmount() {
    this.props.dispatch({ type: USER.CLEAR_SIGNATURE_DATA });
  }

  getData = () => this.props.report.contractorWeeklyReportData || [];

  handleClear = (e) => {
    e.preventDefault();
    if (this.state.signature) this.setState({ signature: "" });
    else this.signPadRef.current?.clear();
  };

  handleUseExisting = async (e) => {
    e.preventDefault();
    const { user, dispatch } = this.props;
    if (user?.userId) await dispatch(userActions.getSignature(user.userId, false));
  };

  handlePrint = async () => {
    const { user, dispatch } = this.props;
    // If a fresh signature was drawn (pad visible, not empty), save it to the
    // user's profile before printing — mirrors ADR's sign-on-submit behavior
    // (AdrSubmitSignatureModalV3).
    if (this.signPadRef.current && !this.signPadRef.current.isEmpty()) {
      const sigData = {
        dataUrl: this.signPadRef.current.getTrimmedCanvas().toDataURL("image/png"),
        createdOn: new Date(),
      };
      if (user?.userId) await dispatch(userActions.addSignature(user.userId, sigData));
      this.setState({ signature: sigData.dataUrl });
    }
    window.print();
  };

  handleDownloadDailyReports = () => {
    const { report, dispatch } = this.props;
    const adrIds = report.assistantDailyReportIds || [];
    if (adrIds.length === 0) return;
    dispatch(cem4601PdfActions.requestPdfs(adrIds));
  };

  render() {
    const { report, jobStamp, projContractorList } = this.props;
    const { projectIdNumber, reName, signDate, signature } = this.state;

    const data = report.contractorWeeklyReportData ? this.getData() : [];
    const weekEnding = report.queryParams?.weekEnding || null;
    const prime = (projContractorList || []).find((c) => c.isPrime);
    // const adrIds = report.assistantDailyReportIds || []; // unused while the batch-download button is disabled

    return (
      <React.Fragment>
        <div className="no-print">
          <TitleBar titleText={"Contractor Weekly Report"} />
        </div>

        <div className="cwp-root">
          <div className="cwp-actions no-print">
            {/* Disabled for now — see CODE-902.
            <button
              type="button"
              className="btn btn-outline-secondary mr-2"
              disabled={adrIds.length === 0}
              onClick={this.handleDownloadDailyReports}
              title={
                adrIds.length === 0
                  ? "No Accepted Daily Reports found for this week"
                  : undefined
              }
            >
              <FontAwesomeIcon icon="download" className="fa-fw mr-1" />
              Download Daily Reports (ZIP)
            </button>
            */}
            <button type="button" className="btn btn-primary" onClick={this.handlePrint}>
              <FontAwesomeIcon icon="print" className="fa-fw mr-1" />
              Print CEM-2513
            </button>
          </div>

          <div className="cwp-header">
            <div className="cwp-agency">
              <div>STATE OF CALIFORNIA &bull; DEPARTMENT OF TRANSPORTATION</div>
              <div className="cwp-title">WEEKLY REPORT OF JOB SITE LABOR</div>
              <div className="cwp-formnum">DOT CEM-2513 (REV 12/2025)</div>
            </div>
            <div className="cwp-weekending">
              <div className="cwp-weekending-label">
                WEEK ENDING DATE
                <br />
                (SATURDAY)
              </div>
              <div className="cwp-weekending-value">
                {weekEnding ? moment(weekEnding).format("MM/DD/YYYY") : ""}
              </div>
            </div>
          </div>

          <table className="cwp-meta-table">
            <thead>
              <tr>
                <th>CONTRACTOR NAME</th>
                <th>CONTRACT NUMBER</th>
                <th>PROJECT IDENTIFIER NUMBER</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{prime ? prime.name : ""}</td>
                <td>{jobStamp?.fullEA}</td>
                <td>
                  <input
                    type="text"
                    className="cwp-input"
                    value={projectIdNumber}
                    onChange={(e) => this.setState({ projectIdNumber: e.target.value })}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="cwp-companies-heading">
            <div>THIS WEEK THE FOLLOWING COMPANIES PERFORMED WORK ON THE JOB SITE</div>
            <div className="cwp-companies-subheading">
              Prime Contractor, Listed and Unlisted Subcontractors, and Trucking (see Note)
            </div>
          </div>

          <table className="cwp-company-table">
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td className="cwp-company-name">&nbsp;</td>
                  <td className="cwp-company-days">
                    <DayCheckboxes checked={DAYS.map(() => false)} none />
                  </td>
                </tr>
              ) : (
                data.map((row, i) => (
                  <tr key={i}>
                    <td className="cwp-company-name">{row.contractorName}</td>
                    <td className="cwp-company-days">
                      <DayCheckboxes checked={daysWorked(row.dates)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="cwp-esign-notice">
            By providing my electronic signature for this form, I agree to conduct business
            transactions by electronic means and that my electronic signature is the legal
            binding equivalent to my handwritten signature. I hereby agree that my electronic
            signature represents my execution or authentication of this form, and my intent to
            be bound by it.
          </div>

          <table className="cwp-sign-table">
            <thead>
              <tr>
                <th>RESIDENT ENGINEER PRINT NAME</th>
                <th>RESIDENT ENGINEER SIGNATURE</th>
                <th>DATE</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    type="text"
                    className="cwp-input"
                    value={reName}
                    onChange={(e) => this.setState({ reName: e.target.value })}
                  />
                </td>
                <td className="cwp-signature-cell">
                  {signature ? (
                    <img
                      src={signature}
                      alt="Resident Engineer Signature"
                      className="cwp-signature-img"
                    />
                  ) : (
                    <SignaturePad
                      canvasProps={{ className: "cwp-signature-pad" }}
                      ref={this.signPadRef}
                    />
                  )}
                  <div className="cwp-signature-actions no-print">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={this.handleUseExisting}
                    >
                      Use Existing
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={this.handleClear}
                    >
                      Clear
                    </button>
                  </div>
                </td>
                <td>
                  <input
                    type="date"
                    className="cwp-input"
                    value={signDate}
                    onChange={(e) => this.setState({ signDate: e.target.value })}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          <div className="cwp-distribution">
            <b>COPY DISTRIBUTION:</b>
            <span>
              <b>Original</b> – Resident Engineer
            </span>
            <span>
              <b>Copy</b> – District Construction Office
            </span>
            <span>
              <b>Copy</b> – Labor Compliance
            </span>
          </div>

          <div className="cwp-footer">
            <div className="cwp-ada-notice">
              <b>ADA Notice</b>
              <span>
                For individuals with sensory disabilities, this document is available in
                alternate formats. For information, call (916) 445-1233, TTY 711, or write to
                Records and Forms Management, 1120 N Street, MS-89, Sacramento, CA 95814.
              </span>
            </div>

            <div className="cwp-footnote">
              This document was generated from the Project Data Management System (ProDMS)
              <br />
              Note: Only data from Accepted Daily Reports is shown.
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  report: state.reports.openReports[ownProps.tab.tabId],
  jobStamp: state.projects.jobStamps[ownProps.tab.projectId],
  projContractorList: state.contractors.projContractorLists[ownProps.tab.projectId] || null,
  user: state.authentication.user,
  existSignatureData: state.users.miscSessionData.userSignature || null,
});

export default connect(mapStateToProps)(ContractorWeeklyReport);
